import React, { useCallback, useEffect, useState } from 'react';
import { Link, useLocation, useParams } from 'react-router-dom';

import RecommendationPanel from '../components/feed/RecommendationPanel/RecommendationPanel';
import { ROUTES } from '../constants';
import { useTranslation } from '../contexts/TranslationContext';
import '../styles/game-recommendations.scss';
import { Game, GameLite, RecommendationFeedItem, RecommendationFeedResponse } from '../types/interfaces';
import { apiGet } from '../utils/api';
import { translateEvent } from '../utils/translateEvent';

function formatHeaderDate(dt: string): string {
  try {
    const d = new Date(dt);
    return d.toLocaleDateString('en-US', { month: 'short', day: 'numeric', year: 'numeric' }) +
      ' · ' + d.toLocaleTimeString('pl-PL', { hour: '2-digit', minute: '2-digit' });
  } catch {
    return dt;
  }
}

const PAGE_SIZE = 25;

const GameRecommendations: React.FC = () => {
  const { gameId } = useParams<{ gameId: string }>();
  const { t } = useTranslation();
  const location = useLocation();
  const gameFromState = (location.state as { game?: Game } | null)?.game ?? null;

  const [items, setItems] = useState<RecommendationFeedItem[]>([]);
  const [total, setTotal] = useState(0);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [page, setPage] = useState(1);

  const fetchRecs = useCallback(async () => {
    if (!gameId) return;
    setLoading(true);
    setError(null);
    try {
      const params = new URLSearchParams({
        page: String(page),
        page_size: String(PAGE_SIZE),
        game_id: gameId,
      });
      const res = await apiGet<RecommendationFeedResponse>(
        `/tipsters/recommendations/feed?${params}`,
      );
      setItems(res.items);
      setTotal(res.total);
    } catch {
      setError(t.gameRecs.loadError);
      setItems([]);
    } finally {
      setLoading(false);
    }
  }, [gameId, page, t]);

  useEffect(() => {
    fetchRecs();
  }, [fetchRecs]);

  const featuredItem = items[0];
  const normalItems = items.slice(1);
  const totalPages = Math.max(1, Math.ceil(total / PAGE_SIZE));

  const gameFromItems: GameLite | undefined = items.find(i => i.bet_event?.game)?.bet_event?.game;
  const game: Game | GameLite | null = gameFromState ?? gameFromItems ?? null;
  const leagueName = gameFromState?.league?.name ?? null;
  const uniqueExperts = new Set(items.map(i => i.tipster_id)).size;

  return (
    <div className="game-recs">
      <div className="game-recs__back">
        <Link to={ROUTES.EXPERTS_RECOMMENDATIONS} className="game-recs__back-link">
          {t.gameRecs.backToFeed}
        </Link>
      </div>

      <header className="game-recs__header">
        <div className="game-recs__header-meta">
          {leagueName && (
            <span className="game-recs__league-pill">{translateEvent(leagueName, t.eventsDictionary)}</span>
          )}
          {game?.datetime && (
            <span className="game-recs__header-time">{formatHeaderDate(game.datetime)}</span>
          )}
        </div>

        <div className="game-recs__matchup">
          <span className="game-recs__team">{game?.home_team ? translateEvent(game.home_team, t.eventsDictionary) : '—'}</span>
          <span className="game-recs__vs">{t.common.vs}</span>
          <span className="game-recs__team">{game?.away_team ? translateEvent(game.away_team, t.eventsDictionary) : '—'}</span>
        </div>

        {!loading && (
          <div className="game-recs__stats">
            <div className="game-recs__stat">
              <span className="game-recs__stat-val">{total}</span>
              <span className="game-recs__stat-label">{t.gameRecs.recommendations}</span>
            </div>
            {uniqueExperts > 0 && (
              <>
                <div className="game-recs__stat-divider" />
                <div className="game-recs__stat">
                  <span className="game-recs__stat-val">{uniqueExperts}</span>
                  <span className="game-recs__stat-label">{t.gameRecs.experts}</span>
                </div>
              </>
            )}
          </div>
        )}
      </header>

      {loading && <p className="game-recs__message">{t.common.loading}</p>}
      {error && !loading && (
        <p className="game-recs__message game-recs__message--error">{error}</p>
      )}
      {!loading && !error && items.length === 0 && (
        <p className="game-recs__message">{t.gameRecs.noExpertPicks}</p>
      )}

      {!loading && !error && items.length > 0 && (
        <>
          <div className="game-recs__section-label">{t.gameRecs.expertPicks}</div>

          <div className="game-recs__grid">
            {featuredItem && (
              <div className="game-recs__featured">
                <RecommendationPanel
                  item={featuredItem}
                  featured
                  formattedEvent={translateEvent(
                    featuredItem.bet_event?.event ?? '',
                    t.eventsDictionary,
                  )}
                />
              </div>
            )}
            {normalItems.map(item => (
              <div key={item.id} className="game-recs__cell">
                <RecommendationPanel
                  item={item}
                  formattedEvent={translateEvent(
                    item.bet_event?.event ?? '',
                    t.eventsDictionary,
                  )}
                />
              </div>
            ))}
          </div>

          {totalPages > 1 && (
            <div className="game-recs__pagination">
              <button
                type="button"
                className="button_secondary"
                disabled={page <= 1}
                onClick={() => setPage(p => Math.max(1, p - 1))}
              >
                {t.common.previous}
              </button>
              <span className="game-recs__pagination-label">
                {t.common.paginationShort
                  .replace('{page}', String(page))
                  .replace('{totalPages}', String(totalPages))}
              </span>
              <button
                type="button"
                className="button_secondary"
                disabled={page >= totalPages}
                onClick={() => setPage(p => p + 1)}
              >
                {t.common.next}
              </button>
            </div>
          )}
        </>
      )}
    </div>
  );
};

export default GameRecommendations;
